import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, fromEvent, merge, Subscription } from 'rxjs';
import { offlineDb } from './offline-db';

const OFFLINE_MESSAGE = "You're offline. Showing your saved data.";

export interface OfflineStatusSnapshot {
  online: boolean;
  /** Sticky UI flag — stays true until online with empty outbox (no flash on retries). */
  displayOffline: boolean;
  pendingCount: number;
  syncing: boolean;
  message: string;
}

@Injectable({ providedIn: 'root' })
export class OfflineStatusService implements OnDestroy {
  private readonly status$ = new BehaviorSubject<OfflineStatusSnapshot>({
    online: typeof navigator !== 'undefined' ? navigator.onLine : true,
    displayOffline: typeof navigator !== 'undefined' ? !navigator.onLine : false,
    pendingCount: 0,
    syncing: false,
    message: '',
  });

  readonly snapshot$ = this.status$.asObservable();
  private sub?: Subscription;

  constructor(private zone: NgZone) {
    if (typeof window === 'undefined') return;
    this.sub = merge(fromEvent(window, 'online'), fromEvent(window, 'offline')).subscribe(() => {
      this.zone.run(() => this.refreshOnline());
    });
    void this.refreshPending();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  get snapshot(): OfflineStatusSnapshot {
    return this.status$.value;
  }

  get isOnline(): boolean {
    return this.status$.value.online;
  }

  refreshOnline(): void {
    if (navigator.onLine) {
      this.markOnline();
    } else {
      this.markOffline();
    }
  }

  /** Force offline after XHR status 0 (WebView often keeps navigator.onLine true). */
  markOffline(): void {
    this.patch({
      online: false,
      displayOffline: true,
      message: OFFLINE_MESSAGE,
    });
  }

  /** Recover after probe / reachable XHR (do not trust navigator.onLine on WebView). */
  markOnline(): void {
    // Keep offline message while sticky banner is up — clearing it mid-retry flashes the UI.
    this.patch({ online: true });
    // Only drop sticky banner when outbox is empty (retries must not hide/show).
    if (this.status$.value.pendingCount === 0) {
      this.hideDisplayOffline();
    }
  }

  setOnline(online: boolean): void {
    if (online) {
      this.markOnline();
    } else {
      this.markOffline();
    }
  }

  setSyncing(syncing: boolean): void {
    this.patch({ syncing });
  }

  async refreshPending(): Promise<number> {
    const pendingCount = await offlineDb.outbox.count();
    // Message follows sticky banner, not the sync online flag.
    const displayOffline = this.status$.value.displayOffline;
    this.patch({
      pendingCount,
      message: displayOffline ? OFFLINE_MESSAGE : '',
    });
    // Online with empty outbox — safe to hide sticky banner now.
    if (this.status$.value.online && pendingCount === 0 && displayOffline) {
      this.hideDisplayOffline();
    }
    return pendingCount;
  }

  private hideDisplayOffline(): void {
    this.patch({ displayOffline: false, message: '' });
  }

  private patch(partial: Partial<OfflineStatusSnapshot>): void {
    const next = { ...this.status$.value, ...partial };
    const cur = this.status$.value;
    if (
      cur.online === next.online &&
      cur.displayOffline === next.displayOffline &&
      cur.pendingCount === next.pendingCount &&
      cur.syncing === next.syncing &&
      cur.message === next.message
    ) {
      return;
    }
    this.status$.next(next);
  }
}
